import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Particles from "@tsparticles/react";
import { loadFull } from "tsparticles";
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  ArrowRight,
  Home,
  CreditCard,
  CalendarDays,
  Sparkles,
  ReceiptText,
  BadgePercent,
} from "lucide-react";
import { Button } from "primereact/button";
import "../assets/css/Thankyou.css";

const formatCurrency = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return null;
  }

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatMethod = (method) => {
  if (!method) return null;

  return String(method)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const getStatusConfig = (paymentStatus) => {
  if (paymentStatus === "success") {
    return {
      eyebrow: "Payment confirmed",
      heading: "You’re all set.",
      description:
        "Your subscription payment has been received. Your plan will be available in your account shortly.",
      icon: CheckCircle2,
      tone: "success",
      badge: "Successful",
    };
  }

  if (paymentStatus === "cancelled") {
    return {
      eyebrow: "Checkout closed",
      heading: "Payment cancelled.",
      description:
        "Your payment was not completed and no charge was confirmed.",
      icon: AlertCircle,
      tone: "cancelled",
      badge: "Cancelled",
    };
  }

  return {
    eyebrow: "Payment incomplete",
    heading: "We couldn’t complete the payment.",
    description:
      "The payment could not be processed or verified. You can safely try again.",
    icon: XCircle,
    tone: "failed",
    badge: "Failed",
  };
};

/** Shared UI for `/thankyou` and `/thankyou-preview`. */
export function PaymentStatusView({
  state: locationState,
  navigate,
}) {
  const particlesInit = useCallback(async (engine) => {
    await loadFull(engine);
  }, []);

  const state = locationState || {};
  const paymentStatus = state.paymentStatus || "failed";
  const verifyData = state.verifyData || {};

  const payment = verifyData.payment || {};
  const subscription =
    verifyData.subscription ||
    state.subscription ||
    {};
  const pricing = verifyData.pricing || {};

  const statusConfig = useMemo(
    () => getStatusConfig(paymentStatus),
    [paymentStatus]
  );

  const StatusIcon = statusConfig.icon;
  const isSuccess = paymentStatus === "success";

  const planName =
    subscription?.plan_name ||
    subscription?.sub_name ||
    "Subscription Plan";

  const planType =
    subscription?.plan_type ||
    "Subscription";

  const durationDays =
    subscription?.duration_days ||
    state.subscription?.duration_days ||
    null;

  const credits =
    subscription?.credits ??
    subscription?.credits_per_month ??
    state.subscription?.credits_per_month ??
    null;

  const finalPrice =
    pricing?.final_price ??
    subscription?.final_price ??
    state.subscription?.final_price ??
    null;

  const regularPrice =
    subscription?.regular_price ??
    state.subscription?.regular_price ??
    null;

  const selectedPrice =
    subscription?.selected_price ??
    state.subscription?.selected_price ??
    finalPrice;

  const isWaitlistOffer = Boolean(
    subscription?.is_waitlist_offer ??
    state.subscription?.is_waitlist_offer
  );

  const waitlistSaving =
    regularPrice != null &&
      selectedPrice != null &&
      Number(regularPrice) > Number(selectedPrice)
      ? Number(regularPrice) - Number(selectedPrice)
      : null;

  const message =
    state.message ||
    (isSuccess
      ? "Your payment was verified successfully."
      : paymentStatus === "cancelled"
        ? "You cancelled the payment before it was completed."
        : "Something went wrong while processing your payment.");

  const details = [
    durationDays
      ? {
        label: "Duration",
        value: `${durationDays} days`,
        icon: CalendarDays,
      }
      : null,

    credits != null
      ? {
        label: "Credits",
        value: `${credits} per month`,
        icon: Sparkles,
      }
      : null,

    payment?.method
      ? {
        label: "Payment method",
        value: formatMethod(payment.method),
        icon: CreditCard,
      }
      : null,

    payment?.payment_id
      ? {
        label: "Payment ID",
        value: payment.payment_id,
        icon: ReceiptText,
        mono: true,
      }
      : null,
  ].filter(Boolean);

  return (
    <main className={`payment-status-page status-${statusConfig.tone}`}>
      {/* {isSuccess && ( */}
        <Particles
          id="thankyou-particles"
          init={particlesInit}
          className="thankyou-particles"
          options={{
            fullScreen: false,
            detectRetina: true,
            particles: {
              number: {
                value: 26,
                density: {
                  enable: true,
                  area: 900,
                },
              },
              color: {
                value: ["#FF4DA6", "#FF71BC", "#24D483", "#FFFFFF"],
              },
              shape: {
                type: "circle",
              },
              opacity: {
                value: {
                  min: 0.12,
                  max: 0.42,
                },
              },
              size: {
                value: {
                  min: 1,
                  max: 3,
                },
              },
              move: {
                enable: true,
                speed: 0.45,
                direction: "none",
                random: true,
                straight: false,
                outModes: {
                  default: "out",
                },
              },
            },
          }}
        />
      {/* )} */}

      <div className="status-ambient status-ambient-one" />
      <div className="status-ambient status-ambient-two" />

      <div className="payment-status-shell">
        <motion.section
          className="payment-status-card"
          initial={{
            opacity: 0,
            y: 24,
            scale: 0.985,
          }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
          }}
          transition={{
            duration: 0.55,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <div className="status-card-topline" />

          <div className="status-card-header">
            <motion.div
              className={`status-icon-wrap ${statusConfig.tone}`}
              initial={{
                scale: 0.8,
                opacity: 0,
              }}
              animate={{
                scale: 1,
                opacity: 1,
              }}
              transition={{
                delay: 0.12,
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <StatusIcon size={36} strokeWidth={1.8} />
            </motion.div>

            <div className="status-copy">
              <div className="status-eyebrow">
                <span className="status-eyebrow-dot" />
                {statusConfig.eyebrow}
              </div>

              <h1>{statusConfig.heading}</h1>

              <p className="status-description">
                {statusConfig.description}
              </p>

              <p className="status-message">
                {message}
              </p>
            </div>

            <div className={`status-badge ${statusConfig.tone}`}>
              {statusConfig.badge}
            </div>
          </div>

          <div className="status-card-body">
            <section className="plan-summary-panel">
              <div className="plan-summary-heading">
                <div>
                  <span className="section-kicker">
                    Selected plan
                  </span>

                  <h2>{planName}</h2>

                  <p>
                    {planType}
                    {durationDays
                      ? ` · ${durationDays} days`
                      : ""}
                  </p>
                </div>

                {isWaitlistOffer && (
                  <div className="waitlist-chip">
                    <BadgePercent size={16} />
                    Waitlist offer
                  </div>
                )}
              </div>

              <div className="price-block">
                <span className="price-label">
                  {isSuccess
                    ? "Amount paid"
                    : "Checkout amount"}
                </span>

                <div className="price-row">
                  {isWaitlistOffer &&
                    regularPrice != null &&
                    Number(regularPrice) >
                    Number(selectedPrice) && (
                      <span className="regular-price">
                        {formatCurrency(regularPrice)}
                      </span>
                    )}

                  <strong>
                    {formatCurrency(finalPrice ?? selectedPrice) ||
                      "—"}
                  </strong>
                </div>

                {isWaitlistOffer &&
                  waitlistSaving != null &&
                  waitlistSaving > 0 && (
                    <span className="saving-note">
                      You saved{" "}
                      {formatCurrency(waitlistSaving)}
                    </span>
                  )}
              </div>
            </section>

            {details.length > 0 && (
              <section className="payment-details-grid">
                {details.map((item) => {
                  const DetailIcon = item.icon;

                  return (
                    <div
                      className="payment-detail-item"
                      key={item.label}
                    >
                      <div className="detail-icon">
                        <DetailIcon
                          size={18}
                          strokeWidth={1.8}
                        />
                      </div>

                      <div>
                        <span>{item.label}</span>
                        <strong
                          className={
                            item.mono
                              ? "detail-value-mono"
                              : ""
                          }
                        >
                          {item.value}
                        </strong>
                      </div>
                    </div>
                  );
                })}
              </section>
            )}

            {subscription?.razorpay_subscription_id && (
              <div className="subscription-reference">
                <span>Subscription reference</span>
                <code>
                  {subscription.razorpay_subscription_id}
                </code>
              </div>
            )}

            <div className="status-actions">
              <Button
                label="Back to Home"
                icon={<Home size={17} />}
                className="status-button secondary-button"
                onClick={() => navigate("/")}
              />

              {isSuccess && (
                <Button
                  label="View Subscription"
                  icon={<ArrowRight size={17} />}
                  iconPos="right"
                  className="status-button primary-button"
                  onClick={() =>
                    navigate("/dashboard/subscriptions")
                  }
                />
              )}
            </div>
          </div>
        </motion.section>

        <p className="payment-support-note">
          Need help with this payment? Contact our support team
          and keep your payment reference handy.
        </p>
      </div>
    </main>
  );
}

export default function PaymentStatusPage() {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <PaymentStatusView
      state={location.state}
      navigate={navigate}
    />
  );
}